import { FileChecksum } from '@rails/activestorage/src/file_checksum'
import justcastApi from './justcast'
import { bulkUploadContentType } from '../utils/bulkUploadFiles'
import { apiErrorMessage } from '../utils/apiErrors'

// Flatten an axios failure into the shape the page reads: status, readable Rails
// validation details, quota fields, and a traceable request id. Keeps Axios
// internals out of the UI.
export const normalizeError = (err) => {
  const res = err.response
  const data = (res && res.data) || {}
  const status = res && res.status
  const serverMessage = apiErrorMessage(data)
  const fallback = status
    ? `The server returned HTTP ${status} without an error message.`
    : ((err && err.message) || 'Request failed')
  const e = new Error(serverMessage || fallback)
  if (res) e.status = res.status
  if (data.limit !== undefined) e.limit = data.limit
  if (data.overage !== undefined) e.overage = data.overage
  const headers = (res && res.headers) || {}
  e.requestId = headers['x-request-id'] || data.request_id || data.requestId
  throw e
}

// Real api client for the bulk-upload orchestrator (src/hooks/useBulkUpload).
// Shape mirrors what runBulkUpload consumes; injected in tests so this file is never exercised there.
export const bulkUploadApi = {
  // POST /v2/shows/:show_id/audioposts/bulk_direct_uploads -> presigned targets
  presign: (showId, blobs) =>
    justcastApi.post(`/v2/shows/${showId}/audioposts/bulk_direct_uploads`, { files: blobs })
      .then((res) => (res.data.blobs || res.data).map((b) => ({
        signed_id: b.signed_id,
        url: b.direct_upload.url,
        headers: b.direct_upload.headers
      })), normalizeError),

  // POST /v2/shows/:show_id/audioposts/bulk -> created episodes
  create: (showId, audioposts) =>
    justcastApi.post(`/v2/shows/${showId}/audioposts/bulk`, { audioposts })
      .then((res) => res.data.audioposts || res.data, normalizeError)
}

export const bulkUploadTransport = {
  checksum: (file) => new Promise((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) return reject(error)
      // File.type varies by browser/OS for the same audio file; the backend expects
      // the canonical mp3/m4a types for bulk direct uploads.
      resolve({ checksum, byteSize: file.size, contentType: bulkUploadContentType(file) })
    })
  }),

  // Plain XHR PUT of the file to the presigned target (same transport @rails/activestorage's BlobUpload uses).
  put: ({ url, headers, file, onProgress = () => {} }) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)
    Object.keys(headers || {}).forEach((key) => xhr.setRequestHeader(key, headers[key]))
    xhr.upload.addEventListener('progress', (e) => e.lengthComputable && onProgress(e.loaded / e.total))
    xhr.addEventListener('load', () =>
      (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)))
    xhr.addEventListener('error', () => reject(new Error('Upload failed')))
    xhr.send(file.slice())
  })
}
